import React from 'react';
import { Formik, Form, Field, FieldProps } from 'formik';
import { Button, TextField, Typography, Box, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import * as Yup from 'yup';
import {Notice, usePostNoticeMutation} from "../../redux/residentPortalApi";
import TinyMCEditor from "../common/TinyMCEditor";

interface FormValues {
  title: string;
  description: string;
  developments: string[];
}

const menuOptions = {
  'Millennium Heights': ['Block 1', 'Block 2', 'Block 3', 'Block 4', 'All'],
  'Pomona City': ['Phase 1A', 'Phase 1B&C', 'All'],
  'Pokugara Residential Estate': ['Townhouses', 'Garden', 'Manor', 'Villa', 'All']
};

const NoticeValidationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required'),
  developments: Yup.array().of(Yup.string()).min(1, 'At least one development must be selected')
});

const Notices: React.FC = () => {
  const [postNotice, { isLoading }] = usePostNoticeMutation();

  const handleSelectChange = (event: SelectChangeEvent<string[]>, setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void) => {
    const {
      target: { value },
    } = event;

    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setFieldValue('developments', selectedValues);
  };

  const handleMenuItemClick = (
    value: string,
    currentValue: string[],
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  ) => {
    const newChecked = [...currentValue];
    const currentIndex = newChecked.indexOf(value);

    // Split the value to get the group and the item
    const [group, item] = value.split(' - ');

    if (item === 'All') {
      if (currentIndex === -1) {
        // If "All" is being toggled and it is not in the list, add all items
        menuOptions[group as keyof typeof menuOptions].forEach(option => {
          const fullOption = `${group} - ${option}`;
          if (!newChecked.includes(fullOption)) {
            newChecked.push(fullOption);
          }
        });
      } else {
        // If "All" is being toggled and it is in the list, remove all items
        menuOptions[group as keyof typeof menuOptions].forEach(option => {
          const fullOption = `${group} - ${option}`;
          const indexToRemove = newChecked.indexOf(fullOption);
          if (indexToRemove !== -1) {
            newChecked.splice(indexToRemove, 1);
          }
        });
      }
    } else {
      if (currentIndex === -1) {
        // If an individual item is being selected
        newChecked.push(value);
      } else {
        // If an individual item is being deselected
        newChecked.splice(currentIndex, 1);
      }

      // After modifying the individual items, check if all items are selected
      const allItems = menuOptions[group as keyof typeof menuOptions].filter(opt => opt !== 'All').map(opt => `${group} - ${opt}`);
      const allSelected = allItems.every(opt => newChecked.includes(opt));
      const allIndex = newChecked.indexOf(`${group} - All`);

      if (allSelected && allIndex === -1) {
        newChecked.push(`${group} - All`);
      } else if (!allSelected && allIndex !== -1) {
        newChecked.splice(allIndex, 1);
      }
    }

    setFieldValue('developments', newChecked);
  };

  // This function will be used within the Formik context with the proper setFieldValue


  return (
    <Box sx={{ marginTop: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Submit a New Development Notice
      </Typography>
      <Formik<FormValues>
        initialValues={{
          title: '',
          description: '',
          developments: []
        }}
        validationSchema={NoticeValidationSchema}
        onSubmit={(values, actions) => {
          const filteredDevelopments = values.developments.filter(development => development != null);

          const payload = {
            ...values,
            developments: filteredDevelopments
          };

          console.log("++++payload++++++",payload);

          postNotice({ notice: payload }).unwrap()
            .then((response: Notice) => {
              console.log('Notice submitted successfully:', response);
              actions.setSubmitting(false);
              actions.resetForm();
            })
            .catch((error: any) => {
              console.error('Error submitting notice:', error);
              actions.setSubmitting(false);
            });
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form style={{ width: '100%', maxWidth: 500 }}>
            <Field as={TextField}
                   name="title"
                   label="Title"
                   fullWidth
                   variant="outlined"
                   margin="normal"
            />

            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel htmlFor="developments">Developments</InputLabel>
              <Select
                multiple
                value={values.developments}
                onChange={(event) => handleSelectChange(event as SelectChangeEvent<string[]>, setFieldValue)}
                input={<OutlinedInput label="Developments" id="developments" />}
                renderValue={(selected: any) => selected.join(', ')}
              >
                {Object.entries(menuOptions).map(([group, options]) => (
                  <React.Fragment key={group}>
                    <MenuItem disabled value={group} style={{ fontWeight: 'bold' }}>
                      {group}
                    </MenuItem>
                    {options.map(option => (
                      <MenuItem key={option} value={`${group} - ${option}`} onClick={(event) => {
                        event.stopPropagation(); // Prevent onSelect from firing
                        handleMenuItemClick(`${group} - ${option}`, values.developments, setFieldValue);
                      }}>
                        <Checkbox checked={values.developments.includes(`${group} - ${option}`)} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))}
                  </React.Fragment>
                ))}
              </Select>
            </FormControl>

            <Field name="description">
              {({ field }: FieldProps<string, FormValues>) => (
                <TinyMCEditor
                  content={field.value}
                  setContent={(content) => setFieldValue('description', content)}
                />
              )}
            </Field>

            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting || isLoading} sx={{ mt: 3 }}>
              Submit Notice
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default Notices;
